import { FormState, FieldState } from 'formstate';
import { observable, computed, action } from 'mobx';
import * as React from 'react';

import * as Validators from '../cc-validators';
import { lazyInject } from '../../../ioc';
import { InvoiceStore } from '../../../stores/invoice.store';
import { RouterStore } from 'src/core/stores/router.store';
import { BusyFormState } from 'src/util/busy-form';
import { CreditCardIssuer } from 'src/api';
import { PaymentGateway } from 'src/statement/stores/payment-gateway.store';

interface Props {
    id: string;
    skipIFrameMessageHandler?: boolean;
}

export class PGCardInfoFormState extends BusyFormState {
    @lazyInject(InvoiceStore) private invoiceStore!: InvoiceStore;
    @lazyInject(RouterStore) private router!: RouterStore;
    @lazyInject(PaymentGateway) private paymentGateway!: PaymentGateway;

    paymentGatewayBaseUrl = '';
    @observable errorMessage?: string;
    @observable mobilePaymentGatewayUrl?: string;
    @observable desktopPaymentGatewayUrl?: string;
    @observable useNewCard: boolean = false;
    @observable needToSelectAnotherCard: boolean = false;
    @observable needToStoreCreditCard: boolean = false;
    @observable isSavedCreditCardFeatureEnabled: boolean = false;
    @observable skipIFrameMessageHandler: boolean = false;

    paymentGatewayResponseType = {
        Error: 0,
        Submit: 1,
        SearsCard: 2,
    };

    postalCode = new FieldState('').validators(Validators.zipPostalCodeValidator);

    form = new FormState({
        postalCode: this.postalCode,
        isBusy: this.isBusy,
    });

    constructor(props: Props) {
        super();
        const payment = this.invoiceStore.payment;
        this.skipIFrameMessageHandler = props.skipIFrameMessageHandler || false;
        if (!payment) {
            return;
        }

        if (!payment.card) {
            payment.card = {};
        }

        const card = payment.card;
        this.postalCode.reset(card.billingZip || '');

        this.form.validate();

        this.invoiceStore.getInvoice(props.id).then((invoice) => {
            this.paymentGatewayBaseUrl = invoice.paymentGatewayBaseUrl || '';
            this.mobilePaymentGatewayUrl = this.paymentGateway.getMobilePaymentGatewayUrl(invoice.usesPaymentsService);
            this.desktopPaymentGatewayUrl = this.paymentGateway.getDesktopPaymentGatewayUrl(invoice.usesPaymentsService);
        });
    }

    convertToCardIssuer = (name: string | undefined): CreditCardIssuer => {
        if (name === undefined) {
            return CreditCardIssuer.NUMBER_0;
        }
        switch (name.toLowerCase()) {
            case 'americanexpress':
                return CreditCardIssuer.NUMBER_3;
            case 'visa':
                return CreditCardIssuer.NUMBER_4;
            case 'mastercard':
                return CreditCardIssuer.NUMBER_5;
            case 'discover':
                return CreditCardIssuer.NUMBER_6;
            default:
                return CreditCardIssuer.NUMBER_0;
        }
    }

    iframeMessageHandler = (event: MessageEvent) => {
        if (this.skipIFrameMessageHandler || !this.postMessageEventIsValid(event)) {
            return;
        }

        const responseType = event.data.Type;
        const responseData = event.data.Data;
        if (responseType === this.paymentGatewayResponseType.Error) {
            // eslint-disable-next-line no-alert
            alert(responseData);
            return;
        }

        if (responseType !== this.paymentGatewayResponseType.Submit) {
            return;
        }

        const payment = this.invoiceStore.payment;
        if (!payment || this.form.hasError || !responseData.Token) {
            return;
        }

        payment.card = {
            token: responseData.Token,
            billingZip: this.postalCode.$.trim(),
            number: responseData.Number,
            name: responseData.CardholderName,
            expMonth: responseData.ExpMonth,
            expYear: responseData.ExpYear,
            issuerName: this.convertToCardIssuer(responseData.IssuerName)
        };

        payment.needToSaveCard = this.needToStoreCreditCard;
        this.withBusy(this.invoiceStore.submitPayment(payment)).then(
            (confirmation) => {
                this.router.history.push(
                    `/invoice/${payment.invoiceNumber}/confirm-payment/${confirmation.number}`
                );
            },
            (err) => {
                this.errorMessage = err;
            }
        );
    };

    postMessageEventIsValid = (event: MessageEvent) => {
        const iframe = document.getElementById('payment-gateway') as HTMLIFrameElement;
        return event.origin === this.paymentGatewayBaseUrl && event.source === iframe.contentWindow;
    };

    clearError = () => {
        this.errorMessage = undefined;
    };

    handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
        e && e.preventDefault();
        if (this.invoiceStore.needToChargeStoredCard) {
            const payment = this.invoiceStore.payment;
            if (!payment) { return; }
            this.invoiceStore.submitPaymentToUseStoredPaymentMethod(payment).then(
                (confirmation) => {
                    this.router.history.push(`/invoice/${payment.invoiceNumber}/confirm-payment/${confirmation.number}`);
                },
                (err) => {
                    this.errorMessage = err;
                }
            );
        } else {
            const pgIframe = document.getElementById('payment-gateway') as HTMLIFrameElement | null;
            if (!pgIframe || !pgIframe.contentWindow) { return; }
            pgIframe.contentWindow.postMessage({ key: 'st.paymentgateway.submit' }, this.paymentGatewayBaseUrl);
        }
    }

    @action
    setUseNewCard = (value: boolean) => { this.useNewCard = value; }

    @action
    setNeedToSelectAnotherCard = (value: boolean) => { this.needToSelectAnotherCard = value; }

    @action
    setNeedToStoreCreditCard = (value: boolean) => { this.needToStoreCreditCard = value; }

    @computed
    get showLoginLink() {
        return this.invoiceStore.showLoginLink;
    }

    @computed
    get loggedIn() {
        return this.invoiceStore.showStoreOption;
    }

    @computed
    get cardInfo() {
        return this.invoiceStore.payment?.card;
    }
}
